import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReadingModal from '../ReadingModal/ReadingModal';
import { askCards } from '../../services/readingService';
import './AiAskBox.css';

const TYPEWRITER_QUESTIONS = [
  'Will I find love this year?',
  'Should I take the new job?',
  'What is he thinking about me?',
  'Is this the right decision?',
  'What does my future look like?',
];

const AiAskBox = ({
  autoFocus = false,
  className = '',
  examples = [],
  prompts = [],
  placeholder = '',
  inputLabel = '',
  language,
  typewriter = TYPEWRITER_QUESTIONS,
  askLabel = 'Ask',
  drawingLabel = 'Drawing...',
  suggestionsLabel = 'Question suggestions',
  errorMessage = 'Something went wrong. Please try again.',
  initialQuestion = '',
  onQuestionChange,
  onAsk,
  onComplete,
  onPrompt,
  moreLabel,
}) => {
  const [question, setQuestion] = useState(initialQuestion);
  const [showAllExamples, setShowAllExamples] = useState(false);
  const collapsible = Boolean(moreLabel) && examples.length > 2;
  const [isAsking, setIsAsking] = useState(false);
  const [error, setError] = useState('');
  const [readingData, setReadingData] = useState(null);
  const [streamingInterpretation, setStreamingInterpretation] = useState('');
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const [userHasTyped, setUserHasTyped] = useState(false);
  const inputRef = useRef(null);
  const interpretationRef = useRef('');

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (placeholder || userHasTyped) return;
    let cancelled = false;
    let timeoutId;

    const sleep = (ms) =>
      new Promise((resolve) => {
        timeoutId = setTimeout(resolve, ms);
      });

    const run = async () => {
      let qi = 0;
      while (!cancelled) {
        const next = typewriter[qi % typewriter.length];
        for (let i = 0; i <= next.length; i++) {
          if (cancelled) return;
          setTypedPlaceholder(next.slice(0, i));
          await sleep(70);
        }
        await sleep(2000);
        for (let i = next.length; i >= 0; i--) {
          if (cancelled) return;
          setTypedPlaceholder(next.slice(0, i));
          await sleep(40);
        }
        await sleep(400);
        qi += 1;
      }
    };

    run();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [placeholder, userHasTyped, typewriter]);

  const handleAsk = useCallback(async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || isAsking) return;
    setError('');
    setIsAsking(true);
    interpretationRef.current = '';
    setStreamingInterpretation('');
    if (onAsk) onAsk();
    await askCards(q, {
      language,
      onCards: (data) => {
        setReadingData(data);
      },
      onToken: (token) => {
        interpretationRef.current += token;
        setStreamingInterpretation(interpretationRef.current);
      },
      onDone: () => {
        setIsAsking(false);
        if (onComplete && interpretationRef.current) onComplete();
      },
      onError: (msg) => {
        setIsAsking(false);
        setError(msg || errorMessage);
      },
    });
  }, [question, isAsking, language, onAsk, onComplete, errorMessage]);

  return (
    <>
      <form className={`hero-ask-form ${className}`.trim()} onSubmit={handleAsk}>
        <input
          ref={inputRef}
          type="text"
          className="hero-ask-input"
          placeholder={placeholder || typedPlaceholder}
          aria-label={inputLabel || askLabel}
          value={question}
          onChange={(e) => {
            setQuestion(e.target.value);
            if (onQuestionChange) onQuestionChange(e.target.value);
            if (!userHasTyped && e.target.value) setUserHasTyped(true);
            if (userHasTyped && !e.target.value) setUserHasTyped(false);
          }}
          disabled={isAsking}
          maxLength={500}
        />
        <button
          type="submit"
          className="hero-ask-btn"
          disabled={!question.trim() || isAsking}
        >
          {isAsking ? drawingLabel : `${askLabel} →`}
        </button>
      </form>
      {error && <p className="hero-ask-error" role="alert">{error}</p>}
      {prompts.length > 0 && (
        <div className="hero-ask-prompts" role="group" aria-label={suggestionsLabel}>
          {prompts.map((prompt) => (
            <button
              key={prompt.id || prompt.label}
              type="button"
              disabled={isAsking}
              onClick={() => {
                setQuestion(prompt.text);
                if (onQuestionChange) onQuestionChange(prompt.text);
                setUserHasTyped(true);
                inputRef.current?.focus();
                if (onPrompt) onPrompt(prompt.id || prompt.label);
              }}
            >
              <span className="hero-ask-prompt-label">{prompt.label}</span>
              <span className="hero-ask-prompt-text">{prompt.text}</span>
            </button>
          ))}
        </div>
      )}
      {prompts.length === 0 && examples.length > 0 && (
        <div className={`hero-ask-examples${collapsible && !showAllExamples ? ' hero-ask-examples-collapsed' : ''}`}>
          {examples.map((example) => (
            <button
              key={example}
              type="button"
              disabled={isAsking}
              onClick={() => {
                setQuestion(example);
                setUserHasTyped(true);
                inputRef.current?.focus();
              }}
            >
              {example}
            </button>
          ))}
          {collapsible && !showAllExamples && (
            <button
              type="button"
              className="hero-ask-more"
              aria-expanded="false"
              onClick={(event) => {
                const list = event.currentTarget.parentElement;
                setShowAllExamples(true);
                requestAnimationFrame(() => list?.children[2]?.focus());
              }}
            >
              {moreLabel}
            </button>
          )}
        </div>
      )}

      {readingData && (
        <ReadingModal
          lang={language}
          cards={readingData.cards}
          verdict={readingData.verdict}
          interpretation={streamingInterpretation}
          isStreaming={isAsking}
          onClose={() => {
            setReadingData(null);
            setStreamingInterpretation('');
          }}
          onAskAnother={() => {
            setReadingData(null);
            setStreamingInterpretation('');
            setQuestion('');
            setUserHasTyped(false);
          }}
        />
      )}
    </>
  );
};

export default AiAskBox;
